import sanitizeHtml from 'sanitize-html'
import { buildServiceLevelSignedFileUrl } from './buildServiceLevelSignedFileUrl'
import type { ServiceLevelNoticePageKey } from './serviceLevelNoticePageUtils'
import { isServiceLevelNoticeVisibleOnPage } from './serviceLevelNoticePageUtils'

type SanitizeHtmlOptions = NonNullable<Parameters<typeof sanitizeHtml>[1]>

export type ServiceLevelNoticeAttachmentRecord = {
  file_path?: string | null
  display_name?: string | null
}

export type ServiceLevelNoticeAttachmentLink = {
  href: string
  label: string
  downloadName: string
}

export type ServiceLevelNoticeRecordForDisplay = {
  id: string
  display_date?: string
  content?: string | null
  tooltip_target_text?: string | null
  tooltip_content?: string | null
  hidden_pages?: unknown
  attachments?: ServiceLevelNoticeAttachmentRecord[] | null
}

export type FormattedServiceLevelNotice = {
  id: string
  displayDateFormatted: string
  contentHtml: string
  tooltipContentHtml: string | null
  attachmentLinks: ServiceLevelNoticeAttachmentLink[]
}

const basenameFromFilePath = (filePath: string): string => {
  const normalized = filePath.replace(/\\/g, '/')
  const name = normalized.split('/').pop() || filePath
  try {
    return decodeURIComponent(name)
  } catch {
    return name
  }
}

/**
 * 本文内のツールチップ対象テキストをspanで囲む
 */
export const processTooltipTarget = (html: string, targetText: string | null): string => {
  if (!targetText) return html
  const escapedTarget = targetText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return html.replace(
    new RegExp(escapedTarget, 'g'),
    `<span class="notice-tooltip-trigger">${targetText}</span>`,
  )
}

export const formatNoticeDisplayDate = (dateStr: string | undefined): string => {
  if (!dateStr) return ''
  return $core.$dayjs(dateStr).format('YYYY年M月D日')
}

export const buildNoticeAttachmentLinks = (
  attachments: ServiceLevelNoticeAttachmentRecord[] | null | undefined,
): ServiceLevelNoticeAttachmentLink[] => {
  if (!attachments?.length) {
    return []
  }
  return attachments
    .filter((att) => att?.file_path)
    .map((att) => {
      const filePath = att.file_path as string
      const label = (att.display_name?.trim() || basenameFromFilePath(filePath)).trim()
      const href = buildServiceLevelSignedFileUrl(filePath)
      return {
        href,
        label,
        downloadName: label,
      }
    })
    .filter((link) => link.href)
}

export const formatServiceLevelNoticeForDisplay = (
  notice: ServiceLevelNoticeRecordForDisplay,
  sanitizeOptions: SanitizeHtmlOptions,
): FormattedServiceLevelNotice => {
  let contentHtml = sanitizeHtml(notice.content || '', sanitizeOptions)
  contentHtml = processTooltipTarget(contentHtml, notice.tooltip_target_text ?? null)
  const tooltipContentHtml = notice.tooltip_content
    ? sanitizeHtml(notice.tooltip_content, sanitizeOptions)
    : null

  return {
    id: notice.id,
    displayDateFormatted: formatNoticeDisplayDate(notice.display_date),
    contentHtml,
    tooltipContentHtml,
    attachmentLinks: buildNoticeAttachmentLinks(notice.attachments),
  }
}

export const filterNoticesVisibleOnPage = <T extends { hidden_pages?: unknown }>(
  notices: T[],
  pageKey: ServiceLevelNoticePageKey,
): T[] => {
  return notices.filter((notice) => isServiceLevelNoticeVisibleOnPage(notice.hidden_pages, pageKey))
}
